/** A refetch for a record kind with membership lists (the DraftProvider's `freshFrom`):
 *  every baseline value as it stands, a membership list both sides changed merged
 *  (`mergedTo`, `mergeIdList`) instead of drifted, and a tombstone (current null, `gone`)
 *  for every entry of a saved row the server no longer has. Rows the draft creates
 *  (negative ids) never drift: the server has never seen them. */
import { draftValuesEqual, parseRecordKey, type FreshValue } from '@/features/records/draft/changeSet';
import type { FreshFrom } from '@/features/records/draft/DraftProvider';

import { isMembershipPath, mergeIdList, type MembershipPaths } from './membership';

/** The `freshFrom` of a kind with these membership lists. Create it once per kind (a
 *  module constant): the DraftProvider re-runs its rebase when it changes. */
export function membershipFreshFrom(memberships: MembershipPaths): FreshFrom {
  return (baselines, entries) => {
    const out: FreshValue[] = [];
    for (const [rec, paths] of Object.entries(baselines)) {
      for (const [path, current] of Object.entries(paths)) {
        const entry = entries[rec]?.[path];
        if (entry && isMembershipPath(path, memberships) && !draftValuesEqual(entry.from, current)) {
          out.push({ rec, path, current, mergedTo: mergeIdList(entry.from, entry.to, current) });
        } else {
          out.push({ rec, path, current });
        }
      }
    }
    // A saved row that vanished: its entries turn stale against nothing — gone, so they can
    // only be dropped (Keep mine has no row to keep them on).
    for (const [rec, paths] of Object.entries(entries)) {
      if (rec in baselines || parseRecordKey(rec).id < 0) continue;
      for (const path of Object.keys(paths)) out.push({ rec, path, current: null, changedBy: null, gone: true });
    }
    return out;
  };
}
